<template>
  <div v-if="visibleButtons.length > 0" class="action-buttons-section">
    <label class="label is-small">Actions</label>
    <div class="buttons action-buttons-row">
      <button
        v-for="button in visibleButtons"
        :key="button.ruleId"
        class="button"
        :class="[getButtonClass(button.color), { 'is-loading': executingButton === button.ruleId }]"
        :disabled="executingButton !== null"
        @click="handleButtonClick(button)"
      >
        <span v-if="button.icon" class="icon is-small">
          <font-awesome-icon :icon="['fal', button.icon]" />
        </span>
        <span>{{ button.label }}</span>
      </button>
    </div>

    <!-- Confirmation Modal - Teleported to body to appear above parent modal -->
    <Teleport to="body">
      <AppModal
        v-model="showConfirmation"
        title="Confirm Action"
        theme="warning"
        size="small"
        confirm-text="Proceed"
        :loading="executingButton !== null"
        @confirm="executeButtonAction"
        @cancel="cancelConfirmation"
      >
        <p>{{ confirmationMessage }}</p>
      </AppModal>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useAdminAutomationRulesStore } from '@/stores/adminAutomationRulesStore';
import AppModal from '@/components/global/AppModal.vue';
import { useToast } from '@/composables/useToast';
import { logger } from '@/services/loggerService';

const props = defineProps({
  deliverable: {
    type: Object,
    required: true
  },
  companyId: {
    type: String,
    default: null
  }
});

const emit = defineEmits(['action-executed']);

const automationStore = useAdminAutomationRulesStore();
const toast = useToast();

// State
const executingButton = ref(null);
const showConfirmation = ref(false);
const confirmationMessage = ref('');
const pendingButton = ref(null);

// Get all enabled button_click rules
const actionButtonRules = computed(() => {
  return automationStore.allRules.filter(
    rule => rule.enabled && rule.trigger?.type === 'button_click'
  );
});

// Evaluate conditions locally to determine which buttons to show
const visibleButtons = computed(() => {
  if (!props.deliverable) return [];

  return actionButtonRules.value
    .filter(rule => {
      // Ensure rule has valid structure before evaluating
      if (!rule || !rule.id) return false;
      try {
        return evaluateConditions(rule.conditions, rule.conditionLogic);
      } catch (error) {
        logger.warn('[DeliverableActionButtons] Error evaluating conditions for rule', {
          ruleId: rule.id,
          error: error.message
        });
        return false;
      }
    })
    .map(rule => ({
      ruleId: rule.id,
      label: rule.trigger?.buttonConfig?.label || rule.name,
      color: rule.trigger?.buttonConfig?.color || 'primary',
      icon: rule.trigger?.buttonConfig?.icon || '',
      confirmationRequired: rule.trigger?.buttonConfig?.confirmationRequired || false,
      confirmationMessage: rule.trigger?.buttonConfig?.confirmationMessage || 'Are you sure you want to proceed?'
    }));
});

// Initialize automation store if needed
onMounted(async () => {
  if (!automationStore.initialized) {
    await automationStore.initialize();
  }
});

// Cleanup
onUnmounted(() => {
  executingButton.value = null;
  showConfirmation.value = false;
});

// Evaluate conditions against the deliverable
function evaluateConditions(conditions, conditionLogic = 'all') {
  if (!conditions || conditions.length === 0) {
    return true; // No conditions = always show
  }

  const evaluateCondition = (condition) => {
    const { field, operator, value } = condition;
    const fieldValue = props.deliverable[field];

    switch (operator) {
      case 'equals':
        return fieldValue === value;
      case 'not_equals':
        return fieldValue !== value;
      case 'in':
        return Array.isArray(value) ? value.includes(fieldValue) : false;
      case 'not_in':
        return Array.isArray(value) ? !value.includes(fieldValue) : true;
      case 'is_empty':
        return fieldValue === null || fieldValue === undefined || fieldValue === '';
      case 'is_not_empty':
        return fieldValue !== null && fieldValue !== undefined && fieldValue !== '';
      default:
        return false;
    }
  };

  if (conditionLogic === 'any') {
    return conditions.some(evaluateCondition);
  }
  return conditions.every(evaluateCondition);
}

// Get Bulma button class from color
function getButtonClass(color) {
  const colorMap = {
    primary: 'is-primary',
    info: 'is-info',
    success: 'is-success',
    warning: 'is-warning',
    danger: 'is-danger',
    link: 'is-link'
  };
  return colorMap[color] || 'is-primary';
}

// Handle button click
function handleButtonClick(button) {
  if (button.confirmationRequired) {
    pendingButton.value = button;
    confirmationMessage.value = button.confirmationMessage;
    showConfirmation.value = true;
  } else {
    executeAction(button);
  }
}

// Execute the action after confirmation
function executeButtonAction() {
  if (pendingButton.value) {
    executeAction(pendingButton.value);
  }
}

// Cancel confirmation
function cancelConfirmation() {
  showConfirmation.value = false;
  pendingButton.value = null;
  confirmationMessage.value = '';
}

// Execute the action
async function executeAction(button) {
  // Validate button and deliverable exist
  if (!button?.ruleId) {
    logger.error('[DeliverableActionButtons] Cannot execute action - invalid button configuration');
    toast.error('Invalid action configuration');
    return;
  }

  if (!props.deliverable?.id) {
    logger.error('[DeliverableActionButtons] Cannot execute action - deliverable not available');
    toast.error('Deliverable not available');
    return;
  }

  // Validate store method exists
  if (typeof automationStore.executeActionButton !== 'function') {
    logger.error('[DeliverableActionButtons] Cannot execute action - store method not available', {
      storeInitialized: automationStore.initialized
    });
    toast.error('Action system not ready. Please refresh the page.');
    return;
  }

  // Validate the rule has valid actions configured
  const rule = actionButtonRules.value.find(r => r.id === button.ruleId);
  if (!rule) {
    logger.error('[DeliverableActionButtons] Rule not found for button', { ruleId: button.ruleId });
    toast.error('Action configuration not found. Please refresh the page.');
    return;
  }

  if (!rule.actions || !Array.isArray(rule.actions) || rule.actions.length === 0) {
    logger.error('[DeliverableActionButtons] Rule has no valid actions configured', {
      ruleId: rule.id,
      ruleName: rule.name
    });
    toast.error('This action has no operations configured.');
    return;
  }

  // Validate each action has a type
  const invalidAction = rule.actions.find(a => !a || typeof a !== 'object' || !a.type);
  if (invalidAction) {
    logger.error('[DeliverableActionButtons] Rule contains invalid action', {
      ruleId: rule.id,
      invalidAction
    });
    toast.error('This action is not configured correctly.');
    return;
  }

  // Re-check conditions before calling server to catch stale state
  if (!evaluateConditions(rule.conditions, rule.conditionLogic)) {
    toast.warning('Deliverable state has changed — this action is no longer available.');
    return;
  }

  executingButton.value = button.ruleId;
  showConfirmation.value = false;

  try {
    logger.info('[DeliverableActionButtons] Executing action', {
      ruleId: button.ruleId,
      deliverableId: props.deliverable.id,
      buttonLabel: button.label
    });

    const result = await automationStore.executeActionButton(button.ruleId, props.deliverable.id);

    if (result?.success) {
      toast.success(`Action "${button.label}" completed successfully`);
      emit('action-executed', { ruleId: button.ruleId, result });
    } else {
      const errorMessage = result?.error || 'Action failed';
      toast.error(errorMessage);
      logger.warn('[DeliverableActionButtons] Action failed', {
        ruleId: button.ruleId,
        error: errorMessage
      });
    }
  } catch (error) {
    const errorMessage = error?.message || 'Unknown error';
    logger.error('[DeliverableActionButtons] Error executing action', {
      ruleId: button.ruleId,
      error: errorMessage
    });
    toast.error(errorMessage);
  } finally {
    executingButton.value = null;
    pendingButton.value = null;
  }
}
</script>

<style lang="scss" scoped>
.action-buttons-section {
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--bulma-border-weak);
}

.action-buttons-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}
</style>
